import { useEffect } from 'react';

const useDragging = (row: HTMLElement | null, programRefs: Map<string, HTMLElement>, inView: boolean): void => {
    const onClickHandler = (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    useEffect(() => {
        if (!row) return;

        let curDown = false;
        let oldScrollLeft = 0;
        let curXPos = 0;

        // variables for adding momentum to the scroll
        let velocity = 0;
        let lastTime: number;
        let lastX: number;
        let requestAnimationFrameId: number;

        const onMouseMove = (e: MouseEvent) => {
            if (curDown === true) {
                programRefs.forEach((ref: HTMLElement) => {
                    ref.addEventListener('click', onClickHandler);
                    ref.dataset.dragging = 'true';
                });
                row.scrollLeft = oldScrollLeft + (curXPos - e.pageX);

                // logic for adding momentum to the scroll
                const x = e.pageX;
                const now = performance.now();
                const dx = x - lastX;
                const dt = now - lastTime;
                velocity = (dx / dt) * 16; // normalize to 60fps
                lastX = x;
                lastTime = now;
            }
        };

        const onMouseDown = (e: MouseEvent) => {
            onClickHandler(e);
            curDown = true;
            curXPos = e.pageX;
            oldScrollLeft = row.scrollLeft;

            // logic for adding momentum to the scroll
            lastX = e.pageX;
            lastTime = performance.now();
            velocity = 0;
            cancelAnimationFrame(requestAnimationFrameId); // cancel inertia

            programRefs.forEach((ref: HTMLElement) => {
                ref.addEventListener('click', onClickHandler);
            });
        };

        const onMouseLeave = () => {
            curDown = false;
        };

        const onMouseUp = (e: MouseEvent) => {
            onClickHandler(e);
            curDown = false;

            programRefs.forEach((ref: HTMLElement) => {
                ref.removeEventListener('click', onClickHandler);
            });

            applyInertia();
        };

        /**
         * Applies an inertia effect to a scrollable row element by gradually reducing
         * the scrolling velocity using a friction factor.
         *
         * The function uses `requestAnimationFrame` to create a smooth animation loop
         * that continues until the velocity falls below a threshold.
         *
         * @remarks
         * - The `velocity` variable determines the initial scrolling speed.
         * - The `friction` constant controls the rate of deceleration (lower values
         *   result in slower deceleration).
         */
        function applyInertia() {
            const friction = 0.95;

            function step() {
                if (!row) return;

                velocity *= friction;
                row.scrollLeft -= velocity;

                if (Math.abs(velocity) > 0.5) {
                    requestAnimationFrameId = requestAnimationFrame(step);
                }
            }

            step();
        }

        programRefs.forEach((ref: HTMLElement) => {
            ref.addEventListener('mousemove', onMouseMove);
            ref.addEventListener('mousedown', onMouseDown);
            ref.addEventListener('mouseleave', onMouseLeave);
            ref.addEventListener('mouseup', onMouseUp);
        });
        return () => {
            programRefs.forEach((ref: HTMLElement) => {
                ref.removeEventListener('mousemove', onMouseMove);
                ref.removeEventListener('mousedown', onMouseDown);
                ref.removeEventListener('mouseleave', onMouseLeave);
                ref.removeEventListener('mouseup', onMouseUp);
            });
        };
    }, [inView]);
};

export default useDragging;
